import { Check, Globe } from "lucide-react";
import { useState } from "react";
import CountryFlag from "~/components/CountryFlag";
import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import type { PromptCountry } from "~/types/PromptTypes";

interface CountrySelectorProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  countries: PromptCountry[];
  className?: string;
}

export default function CountrySelector({
  value,
  onValueChange,
  countries,
  className,
}: CountrySelectorProps) {
  const [open, setOpen] = useState(false);
  const selected = countries.find((country) => country.code === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={className}>
          {selected ? (
            <CountryFlag code={selected.code} className="mr-2" />
          ) : (
            <Globe className="w-4 h-4 text-muted-foreground mr-2" />
          )}
          <span className="text-sm">
            {selected ? selected.name : "All Countries"}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1" align="start">
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => {
              onValueChange(null);
              setOpen(false);
            }}
            className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-accent cursor-pointer"
          >
            <Globe className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1 text-left">All Countries</span>
            {!selected && <Check className="w-4 h-4 text-muted-foreground" />}
          </button>
          {countries.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => {
                onValueChange(country.code);
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-accent cursor-pointer"
            >
              <CountryFlag code={country.code} />
              <span className="flex-1 text-left">{country.name}</span>
              {selected?.code === country.code && (
                <Check className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
